// /admin/da-proporre — Proposte: le case da far vedere, cliente per cliente.
//
// La regola che spiega tutta la pagina: **prima le agenzie che dichiarano, sempre.** I posti in vista (tre, si
// cambiano in Impostazioni) si riempiono dal gruppo 1, poi dal 2, poi dal 3 — e se non c'è niente di buono
// restano vuoti. Nessun riempitivo: meglio due case buone che cinque dubbie.
//
// Il conto non si fa qui: arriva già fatto da GET /da-proporre/{cliente} (backend/proposte.py), perché la
// definizione di «proponibile» deve esistere in un posto solo.
//
// La rotta si chiama /da-proporre e non /proposte perché in questa app «proposta» è già la proposta d'acquisto.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { euro } from '../../utils/scheda';
import { giornoEMese, leggiDataUtc } from '../../utils/percorso';
import Icona from '../../components/cliente/Icone';
import { errore } from '../../components/scheda/comuni';
import { PORTALI } from '../../components/scheda/Ricerche';
import './da-proporre.css';

// Oltre una settimana senza rileggere la pagina sul portale lo diciamo: un prezzo di dieci giorni fa non è un
// prezzo, e una casa venduta tre settimane fa non si propone. `giorni_da_controllo` è null quando non l'abbiamo
// mai riletta — il caso di tutte le candidate di prima del 20/10/2026.
export const GIORNI_AVVISO = 7;
export const stantia = (casa) => casa?.giorni_da_controllo === null || casa?.giorni_da_controllo === undefined
  || casa.giorni_da_controllo >= GIORNI_AVVISO;
export const testoControllo = (casa) => (casa?.giorni_da_controllo === null || casa?.giorni_da_controllo === undefined
  ? 'mai ricontrollata'
  : `non ricontrollata da ${casa.giorni_da_controllo} giorni`);

const GRUPPI = {
  1: ['g1', 'Agenzia che dichiara'],
  2: ['g2', 'Agenzia da verificare'],
  3: ['g3', 'Altra agenzia lavorabile'],
};
const MOTIVI_SCARTO = ['troppo cara', 'zona sbagliata', 'stato dell’immobile', 'piano', 'altro'];
const soldi = (v) => (v || v === 0 ? euro(Math.round(v)) : '—');
const quando = (iso) => (iso ? giornoEMese(leggiDataUtc(iso)) : '');
// «fra una settimana», la data che serve a «Più tardi»
const fraGiorni = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

// ---------------------------------------------------------------- la foto, con il riquadro di sempre
const Foto = ({ casa }) => {
  const [rotta, setRotta] = useState(false);
  const src = (casa.foto || [])[0];
  if (!src || rotta) {
    return (
      <div className="im vuota" data-testid={`foto-vuota-${casa.id}`}>
        <span>foto non disponibile{PORTALI[casa.portale] ? <i>{PORTALI[casa.portale]}</i> : null}</span>
      </div>
    );
  }
  return <img className="im" src={src} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setRotta(true)} />;
};

// ---------------------------------------------------------------- una casa
const Casa = ({ casa, onFatto }) => {
  const [parere, setParere] = useState('');
  const [chiedi, setChiedi] = useState(false);     // «Chiami prima?»
  const [scarto, setScarto] = useState(false);
  const [attesa, setAttesa] = useState(false);
  const [classe, etichetta] = GRUPPI[casa.gruppo] || GRUPPI[2];

  const proponi = async () => {
    setAttesa(true);
    try {
      const r = await axios.post(`${API}/proposte-automatiche/${casa.id}/proponi`, { parere: parere.trim() || undefined });
      const collegate = r.data?.case_collegate;
      toast.success(collegate > 1 ? `Proposta. Collegate ${collegate} case di questa agenzia` : 'Proposta al cliente');
      onFatto();
    } catch (e) { errore(e); setAttesa(false); }
  };

  const azione = async (percorso, corpo, parole) => {
    setAttesa(true);
    try {
      const r = await axios.post(`${API}/da-proporre/${casa.id}/${percorso}`, corpo);
      toast.success(r.data?.case > 1 ? `${parole} (${r.data.case} schede della stessa casa)` : parole);
      onFatto();
    } catch (e) { errore(e); setAttesa(false); }
  };

  const scostamento = casa.scostamento;
  const confronto = casa.confronto === 'non disponibile'
    ? <span className="muto">confronto non disponibile</span>
    : (
      <b className={scostamento <= 0 ? 'giu' : 'su'}>
        {casa.euro_metro ? `${euro(casa.euro_metro)}/m²` : ''}
        {scostamento === null || scostamento === undefined ? '' :
          `, ${Math.abs(Math.round(scostamento))}% ${scostamento <= 0 ? 'sotto' : 'sopra'} la mediana di ${casa.comune}`}
      </b>
    );

  return (
    <article className={`dp-casa ${classe}`} data-testid={`casa-${casa.id}`}>
      <Foto casa={casa} />
      <div style={{ minWidth: 0 }}>
        <div className="dp-gruppo"><span className={`gcap ${classe}`}>{etichetta}</span>
          {casa.quasi && <span className="tag t-w">quasi adatta · {casa.quasi.etichette.join(' · ')}</span>}
        </div>
        <div className="r1"><span className="pr">{soldi(casa.prezzo)}</span>
          <span className="mq">{casa.superficie ? `${casa.superficie} m² · ` : ''}{confronto}</span></div>
        <div className="r2">
          {[casa.locali && <b key="l">{casa.locali} locali</b>,
            // il portale a volte scrive già «piano terra»: «piano piano terra» no
            casa.piano && (/piano/i.test(String(casa.piano)) ? String(casa.piano) : `piano ${casa.piano}`),
            casa.classe_energetica && `classe ${casa.classe_energetica}`,
            casa.spese ? `spese ${soldi(casa.spese)}/mese` : null]
            .filter(Boolean).reduce((a, x, i) => (i ? [...a, ' · ', x] : [x]), [])}
        </div>
        <div className="pills">
          {casa.data && <span className="tag t-b">online dal {quando(casa.data)}</span>}
          {casa.ribasso ? <span className="tag t-g">ribasso {soldi(casa.ribasso)}</span> : null}
          {casa.anche_su?.length > 0 && (
            <span className="tag t-o" data-testid={`anche-su-${casa.id}`}>
              Anche su {casa.anche_su.map((p) => PORTALI[p] || p).join(', ')}
            </span>
          )}
        </div>
        <div className="perche" data-testid={`perche-${casa.id}`}>
          {casa.motivi.map((m) => <span key={m}>{m}</span>)}
        </div>
        <div className="dp-ag">
          <span className="nm">{casa.agenzia.nome}</span>
          {!casa.agenzia.in_anagrafica && <span className="muto">non ancora in anagrafica</span>}
          {!casa.agenzia.telefono && <span className="muto">numero non disponibile: aprilo dall’annuncio</span>}
          {casa.punteggio ? <span className="pt">punteggio <b>{casa.punteggio}</b></span> : null}
        </div>

        <div className="dp-azioni">
          <button type="button" className="btn p sm" disabled={attesa}
            onClick={() => (casa.gruppo === 2 && !chiedi ? setChiedi(true) : proponi())}>Proponi al cliente</button>
          <a className="btn sm" href={casa.link} target="_blank" rel="noopener noreferrer">Chiama l’agenzia</a>
          <button type="button" className="btn q" disabled={attesa} onClick={() => setScarto(!scarto)}>Scarta</button>
          <button type="button" className="btn q" disabled={attesa}
            onClick={() => azione('piu-tardi', { fino: fraGiorni(14) }, 'Rimandata di due settimane')}>Più tardi</button>
          {casa.anche_su?.length > 0 && (
            <button type="button" className="btn q" disabled={attesa}
              onClick={() => azione('non-doppione', { altro_id: casa.ids[1] }, 'Separate')}>Non è la stessa casa</button>
          )}
        </div>

        {chiedi && (
          <div className="dp-invito" data-testid={`chiami-prima-${casa.id}`}>
            <b>Questa agenzia non ha ancora dichiarato. Chiami prima?</b>
            <p>Una telefonata la porta nel gruppo 1, e vale per tutte le sue case.</p>
            <div className="dp-azioni">
              <a className="btn p sm" href={casa.link} target="_blank" rel="noopener noreferrer">Chiama ora</a>
              <button type="button" className="btn sm" onClick={proponi} disabled={attesa}>Proponi lo stesso</button>
            </div>
          </div>
        )}

        {scarto && (
          <div className="dp-scarto" data-testid={`scarto-${casa.id}`}>
            {MOTIVI_SCARTO.map((m) => (
              <button type="button" key={m} className="chip" onClick={() => azione('scarta', { motivo: m }, 'Scartata')}>{m}</button>
            ))}
          </div>
        )}

        <div className="parere">
          <input value={parere} onChange={(e) => setParere(e.target.value)} maxLength={400}
            placeholder="Parere breve per il cliente (facoltativo)" aria-label="Parere per il cliente" />
        </div>
      </div>
    </article>
  );
};

// ---------------------------------------------------------------- la pagina
const DaProporre = () => {
  const { clienteId } = useParams();
  const vai = useNavigate();
  const [elenco, setElenco] = useState(null);
  const [sezione, setSezione] = useState(null);
  const [occasioni, setOccasioni] = useState(false);
  const [quasi, setQuasi] = useState(false);
  const [agenzie, setAgenzie] = useState([]);
  const [misura, setMisura] = useState(null);
  const [archiviate, setArchiviate] = useState(null);   // null = non chieste, [] = chieste e vuote

  const caricaElenco = useCallback(() => {
    axios.get(`${API}/da-proporre`).then((r) => setElenco(r.data)).catch(errore);
    axios.get(`${API}/da-proporre/agenzie-da-chiamare`).then((r) => setAgenzie(r.data.agenzie || [])).catch(() => {});
    axios.get(`${API}/da-proporre-misura`).then((r) => setMisura(r.data)).catch(() => {});
  }, []);
  useEffect(() => { caricaElenco(); }, [caricaElenco]);

  const caricaSezione = useCallback(() => {
    if (!clienteId) { setSezione(null); setArchiviate(null); return; }
    setArchiviate(null);
    axios.get(`${API}/da-proporre/${clienteId}`, { params: { occasioni, quasi } })
      .then((r) => setSezione(r.data)).catch(errore);
  }, [clienteId, occasioni, quasi]);
  useEffect(() => { caricaSezione(); }, [caricaSezione]);

  const aggiorna = () => { caricaSezione(); caricaElenco(); };
  const clienti = [...(elenco?.con_case || []), ...(elenco?.senza_case || [])];

  return (
    <main className="st wrap dp" data-testid="pagina-DaProporre">
      <div className="dp-testa">
        <div>
          <div className="k">Le case da far vedere</div>
          <h1>Proposte</h1>
          <p>Prima le agenzie che dichiarano. {elenco ? `${elenco.case_in_vista} case in vista per cliente.` : ''}</p>
        </div>
      </div>

      <div className="dp-lay">
        <aside className="dp-elenco">
          <div className="h">Con case da proporre</div>
          {(elenco?.con_case || []).map((c) => (
            <button type="button" key={c.cliente_id} className={`dp-voce${c.cliente_id === clienteId ? ' on' : ''}`}
              data-testid={`cliente-${c.cliente_id}`} onClick={() => vai(`/admin/da-proporre/${c.cliente_id}`)}>
              <b>{c.nome}</b>
              <span>{c.in_vista} in vista · {c.adatte} adatte in attesa</span>
              <i>{c.dichiarano > 0 ? `${c.dichiarano} da agenzie che dichiarano` : 'nessuna dichiara'}</i>
            </button>
          ))}
          {elenco?.senza_case?.length > 0 && (
            <>
              <div className="h" style={{ marginTop: '.6rem' }}>Senza case adatte</div>
              {elenco.senza_case.map((c) => (
                <button type="button" key={c.cliente_id} className={`dp-voce muta${c.cliente_id === clienteId ? ' on' : ''}`}
                  onClick={() => vai(`/admin/da-proporre/${c.cliente_id}`)}>
                  <b>{c.nome}</b><span>{c.perche?.testo}</span>
                </button>
              ))}
            </>
          )}
          {elenco && clienti.length === 0 && <p className="vuoto">Nessun cliente con ricerca attiva.</p>}

          {misura && (
            <section className="dp-misura" data-testid="misura">
              <div className="h">Questa settimana</div>
              {Object.entries(misura.nomi).map(([chiave, nome]) => (
                <div className="r" key={chiave}>
                  <span className="n">{misura.settimana[chiave]}</span>
                  <div><b>{nome}</b><span>
                    {misura.scorsa[chiave] === misura.settimana[chiave] ? 'come la scorsa'
                      : `${misura.settimana[chiave] - misura.scorsa[chiave] > 0 ? '+' : ''}${misura.settimana[chiave] - misura.scorsa[chiave]} sulla scorsa`}
                  </span></div>
                </div>
              ))}
            </section>
          )}

          {agenzie.length > 0 && (
            <section className="dp-chiamare" data-testid="agenzie-da-chiamare">
              <div className="h">Agenzie da chiamare</div>
              {agenzie.slice(0, 6).map((a) => (
                <div className="r" key={a.nome + a.case}>
                  <span className="n">{a.case}<small>case</small></span>
                  <div><b>{a.nome}</b><span>{a.clienti} client{a.clienti === 1 ? 'e' : 'i'}{a.portali?.length > 1 ? ` · ${a.portali.length} portali` : ''}</span></div>
                  <a className="btn sm" href={a.link} target="_blank" rel="noopener noreferrer">Apri</a>
                </div>
              ))}
            </section>
          )}
        </aside>

        <div className="dp-scheda">
          {!clienteId && <p className="vuoto">Scegli un cliente a sinistra.</p>}
          {sezione && (
            <>
              <div className="dp-cliente">
                <div>
                  <h2>{sezione.cliente.nome}</h2>
                  <div className="meta" data-testid="conti">
                    {sezione.in_vista.length} in vista · {sezione.adatte} adatte in attesa · {sezione.candidate} candidate
                  </div>
                </div>
                <div className="dp-filtri">
                  <label className="chip"><input type="checkbox" checked={quasi} onChange={(e) => setQuasi(e.target.checked)} /> includi quasi adatte</label>
                  <label className="chip"><input type="checkbox" checked={occasioni} onChange={(e) => setOccasioni(e.target.checked)} /> occasioni</label>
                </div>
              </div>

              {sezione.criteri_larghi && (
                <section className="card warn" data-testid="criteri-larghi">
                  <h3>La ricerca di {sezione.cliente.nome} trova {sezione.criteri_larghi.quante} case: i criteri sono troppo larghi</h3>
                  <div className="rows">
                    {sezione.criteri_larghi.tagli.map((t) => (
                      <div key={t.cosa}><span className="tag t-w flat">−{t.quante}</span><div><b>{t.cosa}</b></div></div>
                    ))}
                  </div>
                  <p className="invito">{sezione.criteri_larghi.invito}</p>
                </section>
              )}

              {sezione.senza_motivo && (
                <section className="card dp-niente" data-testid="senza-case">
                  <b>Nessuna casa adatta ora per {sezione.cliente.nome}</b>
                  <p>{sezione.senza_motivo.testo}</p>
                  {sezione.senza_motivo.agenzie?.length > 0 && (
                    <div className="dp-azioni">
                      {sezione.senza_motivo.agenzie.map((a) => (
                        <span className="chip" key={a.nome}>{a.nome} · {a.case} case</span>
                      ))}
                    </div>
                  )}
                </section>
              )}

              {sezione.in_vista.map((c) => <Casa key={c.id} casa={c} onFatto={aggiorna} />)}

              {sezione.adatte > sezione.in_vista.length && (
                <p className="note">Altre <b>{sezione.adatte - sezione.in_vista.length}</b> adatte in attesa: entrano appena si libera un posto.</p>
              )}

              {sezione.escluse?.length > 0 && (
                <details className="dp-escluse" data-testid="escluse">
                  <summary><b>Escluse</b> — {sezione.escluse.length} agenzie non lavorabili,
                    {' '}{sezione.escluse.reduce((n, e) => n + e.case, 0)} case</summary>
                  <div className="b">
                    {sezione.escluse.map((e) => (
                      <div key={e.agenzia}><b>{e.agenzia}</b> — {e.case} case · <span>{e.motivo}</span></div>
                    ))}
                  </div>
                </details>
              )}

              <div className="dp-archivio">
                {archiviate === null ? (
                  <button type="button" className="btn sm" data-testid="mostra-archiviate"
                    onClick={() => axios.get(`${API}/da-proporre/${clienteId}/archiviate`)
                      .then((r) => setArchiviate(r.data.archiviate || [])).catch(errore)}>Mostra archiviate</button>
                ) : (
                  <>
                    <b>In archivio: {archiviate.length}</b>
                    <p className="note">Non si cancellano mai. Se una torna buona, si rimette in gioco.</p>
                    {archiviate.slice(0, 20).map((a) => (
                      <div className="r" key={a.id}>
                        <div><b>{a.titolo || a.comune}</b><span>{soldi(a.prezzo)} · {PORTALI[a.portale] || a.portale} · trovata il {quando(a.data)}</span></div>
                        <button type="button" className="btn sm" onClick={() => axios.post(`${API}/da-proporre/${a.id}/ripristina`)
                          .then(() => { toast.success('Rimessa in gioco'); setArchiviate(archiviate.filter((x) => x.id !== a.id)); aggiorna(); })
                          .catch(errore)}>Ripristina</button>
                      </div>
                    ))}
                    {archiviate.length === 0 && <p className="vuoto">Nessuna casa in archivio.</p>}
                  </>
                )}
              </div>

              <p className="note">Un collaboratore vede qui solo i suoi clienti. <Link to="/admin/impostazioni#da-proporre">Impostazioni</Link> per cambiare quante case stanno in vista.</p>
            </>
          )}
        </div>
      </div>
    </main>
  );
};

export default DaProporre;
